/**
 * Zwergenwald - gemeinsame Variantenwechsel-Logik fuer die eigenen Produktbloecke
 * (blocks/zw-preis-uvp.liquid, blocks/zw-set-ersparnis.liquid, blocks/zw-lieferinfo.liquid).
 *
 * Muster 1:1 von Horizon uebernommen (assets/product-inventory.js, assets/product-price.js):
 *   - Horizons Variantenauswahl (assets/variant-picker.js) loest ein ProductSelectEvent aus,
 *     holt die Produktseite fuer die neue Variante neu vom Server und loest
 *     event.promise mit { detail: { html } } auf. html ist das fertige, neue Dokument.
 *   - Jeder Block hoert auf der naechsten Section (oder dem Schnellkauf-Dialog) zu,
 *     sucht sich im neuen HTML sein Gegenstueck und gleicht sich per morph() daran an.
 *
 * Zuordnung ueber die Block-ID: Jeder Block rendert
 *   <zw-variantenblock data-zw-variantenblock="{{ block.id }}" data-product-id="...">
 * Horizons Preis-Block ordnet genauso ueber die Block-ID zu (product-price.js).
 *
 * Geladen wird die Datei von jedem zw-Block mit demselben <script type="module" src="...">.
 * Der Browser fuehrt ein Modul mit derselben Adresse nur einmal aus - auch bei drei Bloecken
 * auf der Seite gibt es also genau eine Definition und keinen doppelten Code.
 *
 * Nach jedem Angleichen feuert das Element das Ereignis "zw:variantenblock-aktualisiert".
 * Bloecke mit eigenem Verhalten (Versand-Countdown in zw-lieferinfo.js) rechnen dann neu.
 *
 * Bloecke ausserhalb der Kaufbox (eigene Section unter der Produktinformation, O-93) tragen
 * data-zw-bereich="seite": Sie hoeren auf dem ganzen Dokument mit. Auf der Produktseite laedt
 * Horizon beim Variantenwechsel die ganze Seite (ohne section_id, variant-picker.js
 * buildRequestUrl), das neue HTML enthaelt also auch ihr Gegenstueck. Im Schnellkauf-Dialog
 * (nur die Produktkarten-Section) fehlt es - dann bleibt der Block einfach, wie er ist.
 */
import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

export const ZW_AKTUALISIERT = 'zw:variantenblock-aktualisiert';

class ZwVariantenblock extends HTMLElement {
  /** @type {Element | Document | null} */
  #bereich = null;

  connectedCallback() {
    this.#bereich = this.dataset.zwBereich === 'seite' ? document : this.closest('.shopify-section, dialog');
    this.#bereich?.addEventListener(StandardEvents.productSelect, this.#beiVariantenwechsel);
  }

  disconnectedCallback() {
    this.#bereich?.removeEventListener(StandardEvents.productSelect, this.#beiVariantenwechsel);
    this.#bereich = null;
  }

  /**
   * @param {Event & { promise?: Promise<any> }} event - ProductSelectEvent aus @shopify/events
   */
  #beiVariantenwechsel = (event) => {
    // Wie Horizon: Auswahl auf Produktkarten (Kollektionsseite) betrifft diesen Block nicht.
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    // Seitenweite Bloecke: Auswahl im Schnellkauf-Dialog betrifft nicht die Produktseite dahinter.
    if (this.#bereich === document && event.target.closest('dialog, quick-add-component')) return;
    if (!event.promise) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const blockId = this.dataset.zwVariantenblock;
        if (!blockId) return;

        const neu = html.querySelector(`[data-zw-variantenblock="${CSS.escape(blockId)}"]`);
        if (!neu) return;

        morph(this, neu, { childrenOnly: true });
        this.dispatchEvent(new CustomEvent(ZW_AKTUALISIERT, { bubbles: true }));
      })
      .catch((fehler) => {
        if (fehler?.name !== 'AbortError') console.warn('[zw-variantenblock] Variantenwechsel fehlgeschlagen:', fehler);
      });
  };
}

if (!customElements.get('zw-variantenblock')) {
  customElements.define('zw-variantenblock', ZwVariantenblock);
}
